import { useContext, useCallback, useState, useEffect } from "react";
import { CardBody, CardContainer, CardHeader, StickyFooterButtons, Collapse } from "best-common-react";
import { PrimaryOutlinedButtonStyled } from "../elements/PrimaryOutlinedButton";
import DugoutCardSection from "./dugout/DugoutCardSection";
import LineupCardApi from "../../httpClients/LineupCardApi";
import { EditCustomCardContext } from "./contexts/EditCustomCardContext";
import FileHelper from "../../helper/FileHelper";
import { RouteConstants } from "../../constants/RouteConstants";
import { withRouter } from "react-router-dom";
import { useAlert } from "../alerts/AlertsContext";
import { trackPromise } from "react-promise-tracker";
import CardsLoadingBlocker from "../../components/loading/CardsLoadingBlocker";
import LineupCardSection from "./lineup/LineupCardSection";
import DownloadButton from "../elements/DownloadButton";

const EditCustomCardComponent = withRouter(({ history }) => {
  const [retDugoutCard, setRetDugoutCard] = useState(null);
  const [retLineupCard, setRetLineupCard] = useState(null);
  const [saveDisabled, setSaveDisabled] = useState(false);
  const [generateDugoutDisabled, setGenerateDugoutDisabled] = useState(true);
  const [generateLineupDisabled, setGenerateLineupDisabled] = useState(true);
  const editCustomCardContext = useContext(EditCustomCardContext);
  const showAlert = useAlert();
  const { dispatch } = editCustomCardContext;
  const { dugoutCardInfo, lineupCardInfo, dugoutEdited, lineupEdited } = editCustomCardContext.state;

  useEffect(() => {
    const dugoutCardFromStorage = JSON.parse(localStorage.getItem("dugoutCardInfo"));
    if (dugoutCardFromStorage) {
      dispatch({
        type: "setDugoutCardInfo",
        dugoutCardInfo: dugoutCardFromStorage,
      });
      setGenerateDugoutDisabled(false);
    }
    const lineupCardFromStorage = JSON.parse(localStorage.getItem("lineupCardInfo"));
    if (lineupCardFromStorage) {
      dispatch({
        type: "setLineupCardInfo",
        lineupCardInfo: lineupCardFromStorage,
      });
      setGenerateLineupDisabled(false);
    }

    return () => {
      localStorage.removeItem("dugoutCardInfo");
      localStorage.removeItem("lineupCardInfo");
    };
  }, []);

  useEffect(() => {
    // Save is disabled only if neither card has been edited
    setSaveDisabled(!dugoutEdited && !lineupEdited);
    // Dugout card generate button disabled based on its own edited state
    setGenerateDugoutDisabled(dugoutEdited);
    // Lineup card generate button disabled based on its own edited state
    setGenerateLineupDisabled(lineupEdited);
  }, [dugoutEdited, lineupEdited]);

  const saveCustomCard = useCallback(() => {
    parseBorders();
    const customCardRequest = {
      dugoutCardInfoDto: dugoutCardInfo,
      customLineupCardDto: lineupCardInfo,
    };
    trackPromise(
      LineupCardApi.saveCustomCards(customCardRequest).then((response) => {
        setGenerateDugoutDisabled(false);
        setGenerateLineupDisabled(false);
        setRetDugoutCard(response.entity.customDugoutCard);
        setRetLineupCard(response.entity.customLineupCard);
        localStorage.setItem("dugoutCardInfo", JSON.stringify(response.entity.customDugoutCard));
        localStorage.setItem("lineupCardInfo", JSON.stringify(response.entity.customLineupCard));
        setSaveDisabled(true);
        dispatch({
          type: "setDugoutEdited",
          dugoutEdited: true,
        });
        dispatch({
          type: "setDugoutCardInfo",
          dugoutCardInfo: response.entity.customDugoutCard,
        });
        dispatch({
          type: "setLineupEdited",
          lineupEdited: true,
        });
        dispatch({
          type: "setLineupCardInfo",
          lineupCardInfo: response.entity.customLineupCard,
        });
      }),
    );
  }, [dugoutCardInfo, lineupCardInfo]);

  const parseBorders = () => {
    if (
      !dugoutCardInfo.outerColorC &&
      !dugoutCardInfo.outerColorM &&
      !dugoutCardInfo.outerColorY &&
      !dugoutCardInfo.outerColorK
    ) {
      dugoutCardInfo.outerColorC = 91;
      dugoutCardInfo.outerColorM = 56;
      dugoutCardInfo.outerColorY = 75;
      dugoutCardInfo.outerColorK = 68;
    } else {
      dugoutCardInfo.outerColorC = parseInt(dugoutCardInfo.outerColorC);
      dugoutCardInfo.outerColorM = parseInt(dugoutCardInfo.outerColorM);
      dugoutCardInfo.outerColorY = parseInt(dugoutCardInfo.outerColorY);
      dugoutCardInfo.outerColorK = parseInt(dugoutCardInfo.outerColorK);
    }

    if (
      !dugoutCardInfo.innerColorC &&
      !dugoutCardInfo.innerColorM &&
      !dugoutCardInfo.innerColorY &&
      !dugoutCardInfo.innerColorK
    ) {
      dugoutCardInfo.innerColorC = 91;
      dugoutCardInfo.innerColorM = 56;
      dugoutCardInfo.innerColorY = 75;
      dugoutCardInfo.innerColorK = 68;
    } else {
      dugoutCardInfo.innerColorC = parseInt(dugoutCardInfo.innerColorC);
      dugoutCardInfo.innerColorM = parseInt(dugoutCardInfo.innerColorM);
      dugoutCardInfo.innerColorY = parseInt(dugoutCardInfo.innerColorY);
      dugoutCardInfo.innerColorK = parseInt(dugoutCardInfo.innerColorK);
    }
  };

  const generateDugoutCard = useCallback(() => {
    if (retDugoutCard !== null) {
      trackPromise(
        LineupCardApi.generateCustomDugoutCardById(retDugoutCard.customCardId)
          .then((response) => {
            FileHelper.downloadFile(response, "pdf");
          })
          .catch((error) => {
            showAlert(`Error generating custom card`, `danger`);
          }),
      );
    } else if (dugoutCardInfo.customCardId !== null) {
      trackPromise(
        LineupCardApi.generateCustomDugoutCardById(dugoutCardInfo.customCardId)
          .then((response) => {
            FileHelper.downloadFile(response, "pdf");
          })
          .catch((error) => {
            showAlert(`Error generating custom card`, `danger`);
          }),
      );
    }
  }, [retDugoutCard, dugoutCardInfo]);

  const generateLineupCards = useCallback(() => {
    const id = retLineupCard?.id ?? lineupCardInfo.id;
    if (!id) return;
    trackPromise(
      Promise.all([
        LineupCardApi.generateCustomLineupCardById(id, true),
        LineupCardApi.generateCustomLineupCardById(id, false),
      ]).then(([homeResponse, awayResponse]) => {
        FileHelper.downloadFile(homeResponse, "pdf");
        FileHelper.downloadFile(awayResponse, "pdf");
      }),
    );
  }, [retLineupCard, lineupCardInfo]);

  const navToCustomCards = () => {
    history.push(RouteConstants.CUSTOM_CARDS);
  };

  return (
    <CardContainer>
      <CardHeader>Edit Card</CardHeader>
      <CardBody>
        <CardsLoadingBlocker />
        <Collapse title="Dugout Card" styles={{ borderColor: "#dee5ed" }} startsOpen>
          <DugoutCardSection />
        </Collapse>
        <Collapse title="Official Lineup Card" styles={{ borderColor: "#dee5ed" }} startsOpen>
          <LineupCardSection />
        </Collapse>
        <div className="mt-2">
          <StickyFooterButtons>
            <div className="d-flex mt-2">
              <DownloadButton text="Dugout Card" onClick={generateDugoutCard} disabled={generateDugoutDisabled} />
              <DownloadButton
                text="Official Lineup Cards"
                onClick={generateLineupCards}
                disabled={generateLineupDisabled}
              />
            </div>
            <PrimaryOutlinedButtonStyled onClick={saveCustomCard} width={69} height={26} disabled={saveDisabled}>
              Save
            </PrimaryOutlinedButtonStyled>
            <PrimaryOutlinedButtonStyled width={80} height={26} onClick={navToCustomCards} disabled={false}>
              Cancel
            </PrimaryOutlinedButtonStyled>
          </StickyFooterButtons>
        </div>
      </CardBody>
    </CardContainer>
  );
});

export default EditCustomCardComponent;
